import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, IconButton, MenuItem, Tooltip, Typography,
} from "@mui/material";
import {
  AddCircleOutlineOutlined, ArrowDownward, ArrowUpward, CloudUploadOutlined, DeleteOutline,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import CustomTextField from "../../../components/ui/Textfield";
import BookingLayout from "../../bookings/add-booking2/Components/AddBookingLayout.tsx";
import CustomStepper from "../../bookings/add-booking2/Components/CustomStepper";
import StepHeader from "../../bookings/add-booking2/Components/StepHeader";
import type { AcademyCourse, AcademyLesson, AcademyModule, AcademyOptions, CourseDraft } from "./types";
import RichTextLessonEditor from "./RichTextLessonEditor";
import { BackButton, InfoLine, StatusPill, useAcademyColors } from "./AcademyUi";

const STEPS = ["Course information", "Modules & lessons"];

const emptyDraft = (): CourseDraft => ({
  title: "", description: "", category: "", status: "draft",
  skill_ids: [], target_group_ids: [], modules: [],
});

const move = <T,>(items: T[], index: number, direction: -1 | 1) => {
  const target = index + direction;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

const lessonPayload = (lesson: AcademyLesson) => ({
  ...(lesson.id ? { id: lesson.id } : {}), title: lesson.title,
  content_type: lesson.content_type, order: lesson.order,
  video_url: lesson.content_type === "video" ? lesson.video_url : null,
  content_text: lesson.content_type === "text" ? lesson.content_text : "",
});

export default function AcademyCourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<CourseDraft>(emptyDraft());
  const [image, setImage] = useState<File | null>(null);
  const [options, setOptions] = useState<AcademyOptions | null>(null);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const { colors, isDarkMode } = useAcademyColors();

  useEffect(() => {
    apiService.get<AcademyOptions>("/api/lms/courses/options/").then((r) => setOptions(r.data));
    if (!editing) return;
    apiService.get<AcademyCourse>(`/api/lms/courses/${id}/`).then(({ data }) => {
      setDraft({ title: data.title, description: data.description, category: data.category,
        status: data.status, skill_ids: data.skills.map((s) => s.id),
        target_group_ids: data.target_groups.map((g) => g.id), modules: data.modules });
      setCurrentImage(data.image);
    }).catch(() => setError("Could not load this course.")).finally(() => setLoading(false));
  }, [editing, id]);

  const updateModule = (index: number, patch: Partial<AcademyModule>) => setDraft((current) => ({
    ...current, modules: current.modules.map((module, i) => i === index ? { ...module, ...patch } : module),
  }));
  const addModule = () => setDraft((current) => ({ ...current, modules: [...current.modules, {
    title: "", description: "", order: current.modules.length, lessons: [],
  }] }));
  const addLesson = (moduleIndex: number) => updateModule(moduleIndex, { lessons: [...draft.modules[moduleIndex].lessons, {
    title: "", content_type: "video", video_url: "", content_text: "", order: draft.modules[moduleIndex].lessons.length,
  }] });
  const updateLesson = (moduleIndex: number, lessonIndex: number, patch: Partial<AcademyLesson>) =>
    updateModule(moduleIndex, { lessons: draft.modules[moduleIndex].lessons.map((lesson, i) => i === lessonIndex ? { ...lesson, ...patch } : lesson) });

  const validate = () => {
    if (!draft.title.trim()) return "Course title is required.";
    for (const module of draft.modules) {
      if (!module.title.trim()) return "Every module needs a title.";
      for (const lesson of module.lessons) {
        if (!lesson.title.trim()) return "Every lesson needs a title.";
        if (lesson.content_type === "video" && !lesson.video_url) return `Add a video URL for “${lesson.title}”.`;
        if (lesson.content_type === "text" && !lesson.content_text?.trim()) return `Add content for “${lesson.title}”.`;
        if (lesson.content_type === "pdf" && !lesson.id && !lesson.pdf_upload) return `Upload a PDF for “${lesson.title}”.`;
      }
    }
    return "";
  };

  const save = async () => {
    const validation = validate();
    if (validation) { setError(validation); return; }
    setSaving(true); setError("");
    try {
      // One multipart request: the backend saves the course, modules, lessons,
      // PDFs and thumbnail in a single transaction, so a failure leaves nothing half-saved.
      const form = new FormData();
      const modules = draft.modules.map((module, mi) => ({
        ...(module.id ? { id: module.id } : {}), title: module.title,
        description: module.description, order: mi,
        lessons: module.lessons.map((lesson, li) => {
          const payload = { ...lessonPayload(lesson), order: li };
          if (lesson.content_type !== "pdf" || !lesson.pdf_upload) return payload;
          const key = `lesson_pdf_${mi}_${li}`;
          form.append(key, lesson.pdf_upload);
          return { ...payload, pdf_upload_key: key };
        }),
      }));
      form.append("payload", JSON.stringify({ title: draft.title, description: draft.description,
        category: draft.category, status: draft.status, skill_ids: draft.skill_ids,
        target_group_ids: draft.target_group_ids, modules }));
      if (image) form.append("image", image);
      if (editing) await apiService.put<AcademyCourse>(`/api/lms/courses/${id}/`, form);
      else await apiService.post<AcademyCourse>("/api/lms/courses/", form);
      toast.success(editing ? "Course updated." : "Course created.");
      navigate("/academy/admin/courses");
    } catch (caught: unknown) {
      const detail = (caught as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(detail || "The course could not be saved. Check the lesson fields and try again.");
    } finally { setSaving(false); }
  };

  const imagePreview = useMemo(() => (image ? URL.createObjectURL(image) : currentImage), [image, currentImage]);
  useEffect(() => () => { if (image && imagePreview) URL.revokeObjectURL(imagePreview); }, [image, imagePreview]);

  const handleNext = () => {
    if (step === 0) {
      if (!draft.title.trim()) { setError("Course title is required."); return; }
      if (!draft.description.trim()) { setError("Course description is required."); return; }
      setError("");
      setStep(1);
      return;
    }
    save();
  };

  const sectionSx = {
    p: { xs: 2, md: 2.5 }, borderRadius: "12px",
    border: `1px solid ${isDarkMode ? colors.primary[700] : "#E2E8F0"}`,
    backgroundColor: isDarkMode ? colors.primary[800] : "#fff",
  } as const;
  const lessonSx = {
    p: 2, mt: 1.5, borderRadius: "10px",
    backgroundColor: isDarkMode ? colors.primary[900] : "#F8F9FA",
    border: `1px solid ${isDarkMode ? colors.primary[700] : "#EDF2F7"}`,
  } as const;
  const addButtonSx = { textTransform: "none", fontWeight: 600, color: "#614438" } as const;

  if (loading || !options) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: "#4285F4" }} /></Box>;
  }

  const courseInformation = (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <CustomTextField required fullWidth label="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
      <CustomTextField required fullWidth multiline minRows={3} label="Description" value={draft.description}
        onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
        <Autocomplete freeSolo options={options.categories} value={draft.category}
          onInputChange={(_, value) => setDraft({ ...draft, category: value })}
          renderInput={(params) => <CustomTextField {...params} label="Category" helperText="Pick an existing category or type a new one." />} />
        <CustomTextField select label="Status" value={draft.status}
          onChange={(e) => setDraft({ ...draft, status: e.target.value as CourseDraft["status"] })}
          helperText="Only published courses are visible to learners.">
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="published">Published</MenuItem>
        </CustomTextField>
      </Box>
      <Autocomplete multiple options={options.skills} getOptionLabel={(option) => option.name}
        value={options.skills.filter((option) => draft.skill_ids.includes(option.id))}
        onChange={(_, value) => setDraft({ ...draft, skill_ids: value.map((item) => item.id) })}
        renderInput={(params) => <CustomTextField {...params} label="Skills" />} />
      <Autocomplete multiple options={options.groups} getOptionLabel={(option) => option.name}
        value={options.groups.filter((option) => draft.target_group_ids.includes(option.id))}
        onChange={(_, value) => setDraft({ ...draft, target_group_ids: value.map((item) => item.id) })}
        renderInput={(params) => <CustomTextField {...params} label="Target groups" helperText="Leave empty to make the course available to all staff." />} />
      <Box sx={{ ...sectionSx, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button component="label" startIcon={<CloudUploadOutlined />}
          sx={{ ...addButtonSx, border: "1px solid", borderColor: "#CBD5E0", borderRadius: "10px", px: 2.5 }}>
          {imagePreview ? "Replace thumbnail" : "Upload thumbnail"}
          <input hidden type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} />
        </Button>
        <Typography variant="body2" color="text.secondary">
          {image?.name || (currentImage ? "Current thumbnail kept" : "No thumbnail selected")}
        </Typography>
      </Box>
    </Box>
  );

  const modulesAndLessons = (
    <Box sx={{ display: "grid", gap: 2 }}>
      {draft.modules.length === 0 && (
        <Box sx={{ ...sectionSx, textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">No modules yet. Add a module to start building the course.</Typography>
        </Box>
      )}
      {draft.modules.map((module, mi) => (
        <Box key={module.id ?? `module-${mi}`} sx={sectionSx}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
            <Typography sx={{ fontWeight: 700, flexGrow: 1 }}>Module {mi + 1}</Typography>
            <Tooltip title="Move up" arrow><span><IconButton size="small" disabled={mi === 0}
              onClick={() => setDraft({ ...draft, modules: move(draft.modules, mi, -1) })}><ArrowUpward fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Move down" arrow><span><IconButton size="small" disabled={mi === draft.modules.length - 1}
              onClick={() => setDraft({ ...draft, modules: move(draft.modules, mi, 1) })}><ArrowDownward fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Remove module" arrow><IconButton size="small" sx={{ color: "#F44336" }}
              onClick={() => setDraft({ ...draft, modules: draft.modules.filter((_, i) => i !== mi) })}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
          </Box>
          <Box sx={{ display: "grid", gap: 2 }}>
            <CustomTextField fullWidth required size="small" label="Module title" value={module.title} onChange={(e) => updateModule(mi, { title: e.target.value })} />
            <CustomTextField fullWidth multiline minRows={2} size="small" label="Description" value={module.description}
              onChange={(e) => updateModule(mi, { description: e.target.value })} />
          </Box>
          {module.lessons.map((lesson, li) => (
            <Box key={lesson.id ?? `lesson-${li}`} sx={lessonSx}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, flexGrow: 1 }}>Lesson {li + 1}</Typography>
                <Tooltip title="Move up" arrow><span><IconButton size="small" disabled={li === 0}
                  onClick={() => updateModule(mi, { lessons: move(module.lessons, li, -1) })}><ArrowUpward fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Move down" arrow><span><IconButton size="small" disabled={li === module.lessons.length - 1}
                  onClick={() => updateModule(mi, { lessons: move(module.lessons, li, 1) })}><ArrowDownward fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Remove lesson" arrow><IconButton size="small" sx={{ color: "#F44336" }}
                  onClick={() => updateModule(mi, { lessons: module.lessons.filter((_, i) => i !== li) })}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2 }}>
                <CustomTextField required size="small" label="Lesson title" value={lesson.title} onChange={(e) => updateLesson(mi, li, { title: e.target.value })} />
                <CustomTextField select size="small" label="Content type" value={lesson.content_type}
                  onChange={(e) => updateLesson(mi, li, { content_type: e.target.value as AcademyLesson["content_type"] })}>
                  <MenuItem value="video">Video</MenuItem><MenuItem value="text">Text</MenuItem><MenuItem value="pdf">PDF</MenuItem>
                </CustomTextField>
              </Box>
              {lesson.content_type === "video" && (
                <CustomTextField fullWidth size="small" label="YouTube or Vimeo URL" value={lesson.video_url || ""}
                  onChange={(e) => updateLesson(mi, li, { video_url: e.target.value })} sx={{ mt: 2 }} />
              )}
              {lesson.content_type === "text" && (
                <RichTextLessonEditor value={lesson.content_text || ""} onChange={(value) => updateLesson(mi, li, { content_text: value })} />
              )}
              {lesson.content_type === "pdf" && (
                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Button component="label" size="small" startIcon={<CloudUploadOutlined />}
                    sx={{ ...addButtonSx, border: "1px solid", borderColor: "#CBD5E0", borderRadius: "10px", px: 2 }}>
                    {lesson.pdf_file ? "Replace PDF" : "Upload PDF"}
                    <input hidden type="file" accept="application/pdf" onChange={(e) => updateLesson(mi, li, { pdf_upload: e.target.files?.[0] || null })} />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {lesson.pdf_upload?.name || (lesson.pdf_file ? "PDF uploaded" : "No file selected")}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
          <Button startIcon={<AddCircleOutlineOutlined />} onClick={() => addLesson(mi)} sx={{ ...addButtonSx, mt: 1.5 }}>Add lesson</Button>
        </Box>
      ))}
      <Button startIcon={<AddCircleOutlineOutlined />} onClick={addModule}
        sx={{ ...addButtonSx, border: "1px dashed", borderColor: "#aa7b67", borderRadius: "10px", py: 1.2 }}>
        Add module
      </Button>
    </Box>
  );

  const lessonTotal = draft.modules.reduce((sum, module) => sum + module.lessons.length, 0);
  const groupNames = options.groups.filter((group) => draft.target_group_ids.includes(group.id)).map((group) => group.name);
  const skillNames = options.skills.filter((skill) => draft.skill_ids.includes(skill.id)).map((skill) => skill.name);

  const preview = (
    <Box>
      <Box sx={{
        height: 150, borderRadius: "12px", mb: 2, display: "flex", alignItems: "center", justifyContent: "center",
        backgroundColor: `${colors.primary[400]}15`, backgroundSize: "cover", backgroundPosition: "center",
        backgroundImage: imagePreview ? `url(${imagePreview})` : "none",
      }}>
        {!imagePreview && <Typography variant="body2" color="text.secondary">No thumbnail</Typography>}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 0.5 }}>{draft.title || "Untitled course"}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{draft.description || "No description yet."}</Typography>
      <InfoLine label="Status"><StatusPill status={draft.status} /></InfoLine>
      <InfoLine label="Category">{draft.category || "—"}</InfoLine>
      <InfoLine label="Target groups">{groupNames.length ? groupNames.join(", ") : "All staff"}</InfoLine>
      <InfoLine label="Skills">{skillNames.length ? skillNames.join(", ") : "—"}</InfoLine>
      <InfoLine label="Content">
        {draft.modules.length} modules · {lessonTotal} lessons
        {draft.modules.map((module, mi) => (
          <Typography key={module.id ?? mi} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {mi + 1}. {module.title || "Untitled module"} ({module.lessons.length})
          </Typography>
        ))}
      </InfoLine>
    </Box>
  );

  return (
    <Box>
      <BackButton onClick={() => navigate("/academy/admin/courses")} />
      <BookingLayout
        onNext={handleNext}
        onBack={() => { setError(""); setStep(0); }}
        isFirstStep={step === 0}
        isLastStep={step === STEPS.length - 1}
        submitting={saving}
        isEdit={editing}
        submitLabel="Create Course"
        sidebarTitle="Course Information"
        stepperComponent={<CustomStepper steps={STEPS} activeStep={step} onStepClick={(index) => { if (index === 0) setStep(0); else if (step === 0) handleNext(); }} />}
        formContentComponent={
          <Box>
            <StepHeader title={editing ? `Edit course · ${STEPS[step]}` : STEPS[step]} />
            {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}
            {step === 0 ? courseInformation : modulesAndLessons}
          </Box>
        }
        sidebarContentComponent={preview}
      />
    </Box>
  );
}
